import { AnalyticsBrowser } from '@segment/analytics-next';
import { IntegrationsInitOptions } from '@segment/analytics-next/dist/types/browser/settings';
import { getSegmentLanguageTag } from '@lambda/reebelo';
import { SegmentEvent } from '../../events';
import { logger } from '@/lib/logger';
import settings from '../../../../settings';
import { isBot } from '@/lib/utils/bot';
import { hasSegmentConsent } from '@/lib/utils/cookie-consent';

export function shouldTrack() {
  return !isBot() && hasSegmentConsent();
}

export const getDefaultProperties = (): Record<string, any> => ({
  store: settings.store,
  language: getSegmentLanguageTag(settings.store),
});

const getIntegrations = (): IntegrationsInitOptions => ({
  'Google Tag Manager': {
    customDomain: `https://${window.location.hostname}/metrics/`,
  },
});

// Segment's analytics.js loads its device-mode destinations (Google Tag
// Manager, Google Ads gtag, TikTok pixel — ~580 KiB) the instant `.load()`
// runs. Defer that until consent is granted AND a tracking call/component
// actually needs it, rather than at module import, so none of it lands on the
// critical path of the initial page load (and never loads at all without
// consent).
let analyticsInstance: AnalyticsBrowser | null = null;

export const getAnalytics = (): AnalyticsBrowser | null => {
  if (typeof window === 'undefined' || !shouldTrack()) {
    return null;
  }

  if (!analyticsInstance) {
    analyticsInstance = AnalyticsBrowser.load(
      {
        writeKey: process.env.NEXT_PUBLIC_SEGMENT_API_KEY as string,
      },
      {
        integrations: getIntegrations(),
      },
    );
  }

  return analyticsInstance;
};

export const trackEvent = (event: SegmentEvent, properties: object) => {
  try {
    const updatedProperties = { ...getDefaultProperties(), ...properties };
    const analytics = getAnalytics();

    if (!analytics) {
      logger.info({ event, updatedProperties }, 'not tracking segment event');

      return;
    }

    logger.debug({ event, updatedProperties }, 'tracking segment event');

    analytics.track(event, updatedProperties);
  } catch (error: any) {
    logger.error({ event, properties, error }, 'error tracking segment event');
  }
};
